import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface ImageSEO {
  primary_alt_he?: string;
  primary_alt_en?: string;
  title_he?: string;
  figcaption_he?: string;
  keywords_he?: string[];
  breed_detected?: string;
  json_ld_imageobject?: string;
}

interface GalleryImage {
  id: string;
  imageUrl: string;
  uploadedAt: string;
  seo?: ImageSEO;
}

interface ImageCarouselProps {
  images: GalleryImage[];
  language: "he" | "en" | "ru";
}

const translations = {
  he: {
    title: "גלריה",
    noImages: "אין תמונות להצגה",
  },
  en: {
    title: "Gallery",
    noImages: "No images to display",
  },
  ru: {
    title: "Галерея",
    noImages: "Нет изображений для показа",
  },
};

export function ImageCarousel({ images, language }: ImageCarouselProps) {
  const t = translations[language];
  const isRTL = language === "he";

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    direction: isRTL ? "rtl" : "ltr",
    align: "start",
    containScroll: false,
    slidesToScroll: 1,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-initialize when images change
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
    }
  }, [emblaApi, images]);

  const getAlt = (image: GalleryImage) => {
    const seo = image.seo;
    if (!seo) return t.title;
    if (language === "he" && seo.primary_alt_he) return seo.primary_alt_he;
    if (language === "en" && seo.primary_alt_en) return seo.primary_alt_en;
    if (seo.breed_detected) return seo.breed_detected;
    return t.title;
  };

  if (!images || images.length === 0) {
    return null;
  }

  const currentImage = images[selectedIndex];

  return (
    <div
      className="p-3 sm:p-6 rounded-2xl mt-4 sm:mt-8"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.5)" }}
    >
      <h3
        className="mb-4 sm:mb-6 text-center text-lg sm:text-xl"
        style={{ color: "#444444" }}
      >
        {t.title}
      </h3>

      <div className="relative">
        {/* JSON-LD Structured Data for SEO */}
        {currentImage?.seo?.json_ld_imageobject && (() => {
          try {
            // Strip script tags then parse/re-serialize to prevent injection
            const raw = currentImage.seo.json_ld_imageobject
              .replace(/<script[^>]*>/gi, "")
              .replace(/<\/script>/gi, "")
              .trim();
            const parsed = JSON.parse(raw);
            return (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(parsed) }}
              />
            );
          } catch {
            return null;
          }
        })()}

        {/* Carousel */}
        <div className="relative" dir={isRTL ? "rtl" : "ltr"}>
          <div
            ref={emblaRef}
            style={{ overflow: "hidden", borderRadius: "1rem" }}
          >
            <div
              className="embla-container"
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
              }}
            >
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="embla-slide"
                  style={{
                    flex: "0 0 100%",
                    minWidth: 0,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  <figure
                    className="relative w-full aspect-square sm:aspect-[4/3] select-none"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.3)" }}
                    itemScope
                    itemType="https://schema.org/ImageObject"
                  >
                    <img
                      src={image.imageUrl}
                      alt={getAlt(image)}
                      title={image.seo?.title_he || t.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                      loading="eager"
                      decoding="async"
                      itemProp="contentUrl"
                    />
                    {image.seo?.figcaption_he && (
                      <figcaption className="sr-only" itemProp="caption">
                        {image.seo.figcaption_he}
                      </figcaption>
                    )}
                  </figure>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={scrollPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-10"
                style={{
                  backgroundColor: "rgba(242, 155, 112, 0.9)",
                  color: "#444444",
                }}
                aria-label="Previous image"
              >
                <ChevronLeft size={24} />
              </button>

              <button
                onClick={scrollNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-10"
                style={{
                  backgroundColor: "rgba(242, 155, 112, 0.9)",
                  color: "#444444",
                }}
                aria-label="Next image"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Dots + Counter */}
        {images.length > 1 && (
          <div className="flex flex-col items-center gap-2 mt-4">
            {/* Dot indicators */}
            <div className="flex gap-2 justify-center">
              {images.map((_, index) => (
                <button
                  key={index}
                  className="w-2.5 h-2.5 rounded-full transition-all"
                  style={{
                    backgroundColor:
                      index === selectedIndex
                        ? "#F29B70"
                        : "rgba(68, 68, 68, 0.3)",
                  }}
                  onClick={() => emblaApi?.scrollTo(index)}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
            {/* Numeric counter */}
            <div style={{ color: "#444444" }} dir="ltr">
              {selectedIndex + 1} / {images.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
